import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { Container, Reveal, PageHeader } from '@/components/ui'
import { H2 } from '@/components/ui/typography'
import VenueLink from '@/components/cards/VenueLink'
import { type Lecture } from '@/content/lectures'
import { pickLocalized } from '@/cms/pickLocale'
import { formatCmsDate } from '@/cms/formatDate'
import SEO from '@/utils/seo'
import { buildLocalePath } from '@/utils/localePath'
import { buildLecturePageJsonLd } from '@/utils/structuredData'
import { previewTokenParam } from '@/utils/previewToken'
import { renderInlineMarkdownWithShortcodes } from '@/utils/inlineMarkdown'
import ShortcodeBlock from '@/components/embed/ShortcodeBlock'
import { cmsMediaIndex } from '@/content/cms/media.generated'
import { cmsMediaSrcSet } from '@/utils/cmsMediaUrl'
import { uuidFromUploadPath } from '@/utils/mediaPath'
import { safeAssetPath, safeExternalUrl } from '@/utils/safeExternalUrl'
import { usePreload } from '@/entry-server'

/**
 * The `sizes` for an image in the lecture BODY.
 *
 * The body column is `<Container size="lg"><div className="max-w-3xl mx-auto">`
 * — 768px at the md breakpoint and the container's own width below it. The
 * value OVERSTATES the width on a narrow phone (the container's side padding
 * takes a slice off the viewport), which is the safe direction: a `sizes`
 * that is too large only costs a few kilobytes, one that is too small makes
 * the browser upscale a too-small file.
 */
const BODY_IMAGE_SIZES = '(min-width: 768px) 768px, 100vw'

/**
 * The `sizes` for the lecture PHOTOS.
 *
 * Two photos sit in a `sm:grid-cols-2` grid capped at 1480px, so each column
 * is ≈50vw on a phone-to-desktop sweep and ≈730px on a wide screen — 50vw
 * overstates it there, which is safe. One photo is capped at 720px.
 */
const PHOTO_GRID_SIZES = '(min-width: 640px) 50vw, 100vw'
const PHOTO_SINGLE_SIZES = '(min-width: 720px) 720px, 100vw'

interface LecturePayload {
  slug: string
  audience: string
  speaker: string
  event_date: string
  title_zhHant: string
  title_zhHans?: string
  title_en: string
  summary_zhHant: string
  summary_zhHans?: string
  summary_en: string
  // SEO overrides. When set, <SEO> prefers them over the human-readable
  // title_* / summary_* fields. Null = "operator didn't override" → fall
  // back. Added 2026-10-17; the public lectures API used to drop these on
  // the wire, so SEO <title>/<meta description> for /en/lectures/<slug>
  // was always the English title/summary — and a 500-char summary broke
  // the audit. Wire-up matches the schema at server/routes/lectures.ts:64-69.
  meta_title_zhHant: string | null
  meta_title_zhHans: string | null
  meta_title_en: string | null
  meta_description_zhHant: string | null
  meta_description_zhHans: string | null
  meta_description_en: string | null
  host_zhHant: string
  host_zhHans?: string
  host_en: string
  venue_zhHant: string
  venue_zhHans?: string
  venue_en: string
  series_zhHant: string
  series_zhHans?: string
  series_en: string
  photo1_url: string
  photo1_width: number | null
  photo1_height: number | null
  photo1_alt_zhHant: string
  photo1_alt_zhHans?: string
  photo1_alt_en: string
  photo2_url: string
  photo2_width: number | null
  photo2_height: number | null
  photo2_alt_zhHant: string
  photo2_alt_zhHans?: string
  photo2_alt_en: string
  external_url: string | null
  /**
   * Official website of the venue / host organisation. Added 2026-10-20.
   * Null when the host has no public website.
   */
  venue_url: string | null
  /**
   * The body — ONE markdown string for the requested locale.
   *
   * 2026-10-29: the 分段落 feature was removed. The body used to be a JSON
   * array of `{h2, p}` sections with a gallery each; it is now plain
   * markdown — paragraphs separated by a blank line, `## ` for a section
   * heading, `![alt](url)` for an image — exactly like an article's body.
   */
  body_md: string
}

interface PublicLectureResp {
  locale: string
  lecture: LecturePayload
}

type Locale = 'zh-Hant' | 'zh-Hans' | 'en'

function pickField<K extends 'title' | 'summary' | 'host' | 'venue' | 'series'>(
  r: LecturePayload,
  field: K,
  locale: Locale,
): string {
  const col = `${field}_${locale.replace('-', '')}` as keyof LecturePayload
  const v = r[col]
  if (typeof v === 'string' && v.length > 0) return v
  for (const fb of ['zh-Hant', 'en'] as Locale[]) {
    if (fb === locale) continue
    const fbc = `${field}_${fb.replace('-', '')}` as keyof LecturePayload
    const fv = r[fbc]
    if (typeof fv === 'string' && fv.length > 0) return fv
  }
  return ''
}

/** Reads the operator-set per-locale meta override (title or description)
 *  from the transformed `Lecture` (the shape the page actually consumes).
 *  Used inside the render because the SEO component sits below the
 *  `useState<{ lecture: Lecture, … }>` boundary and doesn't have direct
 *  access to the raw API payload. Same chain as the rest of the
 *  pickLocalized helpers on this page: current locale → zh-Hant → en
 *  → the visible `fallback` title/description. 2026-10-17: wired to
 *  clear the audit's overlong-desc partials on /en/lectures/<slug>. */
function metaTitleFor(lecture: Lecture, locale: Locale, fallback: string): string {
  if (locale === 'zh-Hant') return lecture.metaTitleZh || lecture.metaTitleEn || lecture.metaTitleHans || fallback
  if (locale === 'zh-Hans') {
    return lecture.metaTitleHans || lecture.metaTitleZh || lecture.metaTitleEn || fallback
  }
  return lecture.metaTitleEn || lecture.metaTitleZh || lecture.metaTitleHans || fallback
}
function metaDescriptionFor(lecture: Lecture, locale: Locale, fallback: string): string {
  if (locale === 'zh-Hant') return lecture.metaDescriptionZh || lecture.metaDescriptionEn || lecture.metaDescriptionHans || fallback
  if (locale === 'zh-Hans') {
    return lecture.metaDescriptionHans || lecture.metaDescriptionZh || lecture.metaDescriptionEn || fallback
  }
  return lecture.metaDescriptionEn || lecture.metaDescriptionZh || lecture.metaDescriptionHans || fallback
}

function apiToLecture(r: LecturePayload, locale: Locale): Lecture {
  return {
    slug: r.slug,
    date: r.event_date,
    audience: r.audience as 'teacher' | 'parent' | 'student',
    speaker: r.speaker,
    // The detail endpoint now returns a zh-Hans variant for every field, so a
    // simplified reader gets simplified text rather than the Traditional copy
    // this used to fall back to unconditionally.
    titleHans: r.title_zhHans || pickField(r, 'title', 'zh-Hans'),
    titleZh: r.title_zhHant || pickField(r, 'title', locale),
    titleEn: r.title_en || pickField(r, 'title', 'en'),
    summaryHans: r.summary_zhHans,
    summaryZh: r.summary_zhHant,
    summaryEn: r.summary_en,
    hostHans: r.host_zhHans,
    hostZh: r.host_zhHant,
    hostEn: r.host_en,
    venueHans: r.venue_zhHans,
    venueZh: r.venue_zhHant,
    venueEn: r.venue_en,
    // 2026-10-20: the host organisation's official website, so the venue
    // line on the detail page links out to it.
    venueUrl: r.venue_url ?? undefined,
    seriesHans: r.series_zhHans,
    seriesZh: r.series_zhHant,
    seriesEn: r.series_en,
    photo1: r.photo1_url,
    photo1Thumb: r.photo1_url,
    photo1Width: r.photo1_width ?? 0,
    photo1Height: r.photo1_height ?? 0,
    photo1AltZh: r.photo1_alt_zhHant,
    // 2026-10-25: the Simplified alt was in the DB all along, but the
    // payload only ever carried the Traditional and English columns, so a
    // 简体中文 reader was served the Traditional alt.
    photo1AltHans: r.photo1_alt_zhHans ?? '',
    photo1AltEn: r.photo1_alt_en,
    // SEO overrides — only present when the operator filled them in. The
    // <SEO> call further down uses `metaTitleZh/Hans/En` and friends
    // through `pickMeta` (defined just above) so an unset field falls
    // back to the visible `title` / `summary`.
    metaTitleZh: r.meta_title_zhHant ?? undefined,
    metaTitleHans: r.meta_title_zhHans ?? undefined,
    metaTitleEn: r.meta_title_en ?? undefined,
    metaDescriptionZh: r.meta_description_zhHant ?? undefined,
    metaDescriptionHans: r.meta_description_zhHans ?? undefined,
    metaDescriptionEn: r.meta_description_en ?? undefined,
    photo2: r.photo2_url,
    photo2Thumb: r.photo2_url,
    photo2Width: r.photo2_width ?? 0,
    photo2Height: r.photo2_height ?? 0,
    photo2AltZh: r.photo2_alt_zhHant,
    photo2AltHans: r.photo2_alt_zhHans ?? '',
    photo2AltEn: r.photo2_alt_en,
    externalUrl: r.external_url ?? undefined,
  }
}

/**
 * One renderable block of a markdown body.
 *
 * The kinds the CMS's rich-text editor (and the importer) can produce:
 * a heading (`## ` / `### `), a paragraph, a list, a quote, or a standalone
 * image.
 */
type MarkdownBlock =
  | { kind: 'heading'; level: 2 | 3; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; ordered: boolean; items: string[] }
  | { kind: 'quote'; text: string }
  | { kind: 'image'; alt: string; src: string }

/**
 * Split a markdown body into renderable blocks.
 *
 * Blank lines separate blocks — the same separator the importer uses between
 * paragraphs — so a 2000-character unbroken paragraph never happens.
 *
 * A block that is nothing but an image becomes an `image` block, which keeps
 * the figure out of the paragraph flow: a photo gets its own line instead of
 * being wedged into a line-height box between two runs of Chinese text.
 */
function splitMarkdownBody(md: string): MarkdownBlock[] {
  if (!md.trim()) return []
  const blocks: MarkdownBlock[] = []
  for (const raw of md.split(/\n{2,}/)) {
    const text = raw.trim()
    if (!text) continue

    // Anchored + non-capturing on the tail, so a match here means the image
    // IS the whole block — a line that is prose with an image mid-sentence
    // stays a paragraph and renders inline.
    const imgOnly = text.match(/^!\[[^\]]*\]\(([^)\s]+)\)$/)
    if (imgOnly) {
      const alt = text.slice(2, text.indexOf(']('))
      blocks.push({ kind: 'image', alt, src: imgOnly[1] ?? '' })
      continue
    }

    const heading = text.match(/^(#{2,3})\s+(.*)$/)
    if (heading) {
      blocks.push({
        kind: 'heading',
        level: heading[1].length >= 3 ? 3 : 2,
        text: (heading[2] ?? '').trim(),
      })
      continue
    }

    if (text.startsWith('> ')) {
      blocks.push({ kind: 'quote', text: text.slice(2).trim() })
      continue
    }

    const lines = text.split('\n')
    if (lines.every((l) => /^[-*]\s+/.test(l) || /^\d+\.\s+/.test(l))) {
      blocks.push({
        kind: 'list',
        ordered: /^\d+\.\s+/.test(lines[0] ?? ''),
        items: lines.map((l) => l.replace(/^([-*]|\d+\.)\s+/, '')),
      })
      continue
    }

    blocks.push({ kind: 'paragraph', text })
  }
  return blocks
}

/**
 * LectureDetailPage — single lecture article view.
 *
 * Fetches the lecture from /api/cms/public/lectures/:slug at mount. The API
 * returns the full lecture (metadata + the locale's markdown body) in one
 * response. The static `LECTURES` fallback in `@/content/lectures` is
 * intentionally empty as of 2026-10-12 — a slug that 404s in the CMS is
 * treated as a 404 to the visitor (Navigate to /lectures) rather than
 * silently rendering an empty page.
 */
export default function LectureDetailPage() {
  const { t, i18n } = useTranslation()
  const { slug = '' } = useParams<{ slug: string }>()
  const locale = (i18n.language || 'zh-Hant') as Locale
  // PR2 Phase 7: prerender passes the full CMS row via PreloadContext; CSR
  // path (preloaded is null) keeps the existing fetch logic untouched.
  const preloaded = usePreload()
  const [dataState, setData] = useState<{ lecture: Lecture; bodyMd: string } | null>(
    preloaded?.kind === 'lecture'
      ? (() => {
          const j = preloaded.data as PublicLectureResp
          return { lecture: apiToLecture(j.lecture, locale), bodyMd: j.lecture.body_md ?? '' }
        })()
      : null,
  )
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Preload path: data already populated at build time, skip the fetch.
    if (preloaded) return
    let cancelled = false
    setData(null)
    setError(null)
    fetch(`/api/cms/public/lectures/${encodeURIComponent(slug)}?locale=${locale}${previewTokenParam()}`)
      .then(async (r) => {
        if (r.status === 404) {
          // Every lecture now lives in the CMS — a 404 here means the slug is
          // genuinely unknown, so surface the not_found state to the visitor.
          if (cancelled) return
          setError('not_found')
          return
        }
        if (!r.ok) throw new Error(`http_${r.status}`)
        const j = (await r.json()) as PublicLectureResp
        if (cancelled) return
        const merged = apiToLecture(j.lecture, locale)
        setData({ lecture: merged, bodyMd: j.lecture.body_md ?? '' })
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'fetch_failed')
      })
    return () => {
      cancelled = true
    }
  }, [slug, locale, preloaded])

  // Only bounce back once the fetch has actually failed. During the initial
  // render `dataState` is still null and there is no static fallback anymore
  // (LECTURES is intentionally empty), so redirecting here would fire before
  // the request resolves and make every lecture unreachable.
  //
  // Use the locale-prefixed path so the bounce target respects the user's
  // current locale. This used to be a root-relative `/lectures`, which only
  // worked because nginx's bare-path regex did a 301 to `/zh-Hant/lectures` —
  // remove that regex and the root-relative form would 404. Going through
  // `buildLocalePath` also handles the `i18n.language === undefined` case
  // (which would otherwise stringify as "undefined" in the URL).
  if (error) {
    return <Navigate to={buildLocalePath('/lectures', locale)} replace />
  }

  const lecture = dataState?.lecture
  const bodyMd = dataState?.bodyMd ?? ''

  if (!lecture) {
    // Loading — render nothing rather than redirecting a valid slug away.
    return null
  }

  // Every locale-specific field on this page goes through `pickLocalized`,
  // which distinguishes 简体中文 from 繁體中文 by SCRIPT, not by the 'zh'
  // prefix. 2026-10-25: the photo alt used to be an `isZh` ternary
  // (`isZh ? altZh : altEn`), which served the Traditional alt to a
  // 简体中文 reader even though the DB holds a correct Simplified alt.
  const lang = i18n.language
  const title = pickLocalized(lang, lecture.titleHans, lecture.titleZh, lecture.titleEn)
  const summary = pickLocalized(lang, lecture.summaryHans, lecture.summaryZh, lecture.summaryEn)
  const venue = pickLocalized(lang, lecture.venueHans, lecture.venueZh, lecture.venueEn)
  const host = pickLocalized(lang, lecture.hostHans, lecture.hostZh, lecture.hostEn)
  const series = pickLocalized(lang, lecture.seriesHans, lecture.seriesZh, lecture.seriesEn)
  // The photo alt has the same three-locale shape as the fields above, so it
  // goes through the same picker. 2026-10-25: it used an `isZh` ternary,
  // which served the Traditional alt to a 简体中文 reader even though the
  // DB holds a correct Simplified alt.
  const photo1Alt = pickLocalized(lang, lecture.photo1AltHans, lecture.photo1AltZh, lecture.photo1AltEn)
  const photo2Alt = pickLocalized(lang, lecture.photo2AltHans, lecture.photo2AltZh, lecture.photo2AltEn)
  // The photo paths and the external URL come from the CMS, so they are
  // validated before they are allowed to become an href or a src — a
  // `javascript:` value would otherwise run script in every visitor's
  // browser. A photo whose path does not pass is not rendered at all: no
  // image is better than a broken one, and a broken one is better than an
  // executable one. The external link degrades to no link — the page
  // itself, and the "back to the archive" link, are the fallbacks.
  const photo1Src = safeAssetPath(lecture.photo1)
  const photo2Src = safeAssetPath(lecture.photo2)
  const externalHref = safeExternalUrl(lecture.externalUrl)
  // The photos are media-library uploads, so the uuid can be recovered from
  // the path and the responsive ladder looked up. A photo whose path is not
  // a library upload (or whose row has no variants) simply gets no srcset
  // and keeps rendering exactly as before.
  const photo1SrcSet = cmsMediaSrcSet(uuidFromUploadPath(photo1Src))
  const photo2SrcSet = cmsMediaSrcSet(uuidFromUploadPath(photo2Src))
  const speaker = lecture.speaker
  const audienceTagKey = `lectures.detail.tag${lecture.audience.charAt(0).toUpperCase() + lecture.audience.slice(1)}`
  const audienceTag = t(audienceTagKey)

  // 2026-10-16: was a try/catch around the parse, which never fired —
  // `new Date(bad)` returns an Invalid Date object instead of throwing, and its
  // `toLocaleDateString()` returns the string "Invalid Date", which shipped to
  // production on /zh-Hant/lectures/yada before this was caught. Same helper as
  // LectureCard. See src/cms/formatDate.ts.
  const formattedDate = formatCmsDate(lecture.date, i18n.language)

  // The body is ONE markdown string, authored in the CMS's rich-text editor
  // (or by the importer), so it is rendered as markdown: `## ` / `### ` for
  // headings, a blank line between paragraphs, `- ` for a list, and
  // `![alt](url)` for an image.
  //
  // 2026-10-29: the body used to be a JSON array of `{h2, p}` sections, each
  // rendered as its own numbered block with a table of contents beside it.
  // The 分段落 feature is gone — the markdown carries the section headings
  // (`## `) and the page renders them where they stand.
  const bodyBlocks = splitMarkdownBody(bodyMd)

  return (
    <article aria-labelledby="lecture-detail-title">
      <SEO
        // 2026-10-17: prefer the operator-set per-locale `meta_title_*` /
        // `meta_description_*` overrides when present. Falls back to the
        // human-readable `title` / `summary` when the operator hasn't
        // filled them in. Same fallback chain as the rest of the
        // pickLocalized helpers on this page.
        title={metaTitleFor(lecture, locale, title)}
        description={metaDescriptionFor(lecture, locale, summary)}
        keywords={t('seo.lecturesKeywords')}
        type="article"
        ogImage={photo1Src}
        articlePublishedTime={lecture.date}
        articleAuthor={speaker}
        jsonLd={buildLecturePageJsonLd({
          lecture,
          headline: title,
          alternativeHeadline: lecture.titleEn !== title ? lecture.titleEn : undefined,
          author: speaker,
          locationName: venue,
          description: summary,
          keywords: ['YADA', '講座 Lectures', venue, host, series].filter(Boolean),
        })}
      />
      <PageHeader
        tag={audienceTag}
        title={title}
        description={summary}
      />

      {/* Meta block */}
      <section className="py-5 px-section-x bg-surface-2 border-b border-border">
        <Container size="lg">
          <Reveal>
            <dl className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm">
              <div className="flex items-start gap-2">
                <Icon icon="lucide:calendar" className="text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <dt className="text-ink-muted text-xs uppercase tracking-wide mb-0.5">
                    {t('lectures.detail.metaDate')}
                  </dt>
                  <dd className="text-ink font-semibold">
                    <time dateTime={lecture.date}>{formattedDate}</time>
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Icon icon="lucide:building-2" className="text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <dt className="text-ink-muted text-xs uppercase tracking-wide mb-0.5">
                    {t('lectures.detail.metaVenue')}
                  </dt>
                  <dd className="text-ink font-semibold">
                    {/*
                      2026-10-20: the venue links to the host organisation's
                      official website, opening in a new tab. Plain text when
                      there is no URL.
                    */}
                    <VenueLink
                      venue={venue}
                      url={lecture.venueUrl}
                      externalLabel={t('lectures.detail.venueWebsite')}
                      className="inline-flex items-center gap-1"
                      linkClassName="hover:text-primary hover:underline underline-offset-2 transition-colors"
                    />
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Icon icon="lucide:user" className="text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <dt className="text-ink-muted text-xs uppercase tracking-wide mb-0.5">
                    {t('lectures.detail.metaSpeaker')}
                  </dt>
                  <dd className="text-ink font-semibold">{speaker}</dd>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Icon icon="lucide:tv" className="text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <dt className="text-ink-muted text-xs uppercase tracking-wide mb-0.5">
                    {t('lectures.detail.metaHost')}
                  </dt>
                  <dd className="text-ink font-semibold">{host}</dd>
                </div>
              </div>
              <div className="flex items-start gap-2 sm:col-span-2 lg:col-span-1">
                <Icon icon="lucide:layers" className="text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <dt className="text-ink-muted text-xs uppercase tracking-wide mb-0.5">
                    {t('lectures.detail.metaSeries')}
                  </dt>
                  <dd className="text-ink font-semibold">{series}</dd>
                </div>
              </div>
            </dl>
          </Reveal>
        </Container>
      </section>

      {/* Photos — single column when only one photo, side-by-side when both.
          Replaces the old "hero photo" + "secondary photo gallery" split so
          multi-photo lectures get a 2-up grid instead of two full-bleed rows.
          The 2-col layout caps the row at ~1480px so each photo sits at ≈720px
          on desktop; mobile stacks them vertically. */}
      {(photo1Src || photo2Src) && (
      <section className="py-section-y px-section-x bg-surface" aria-labelledby="lecture-photos-heading">
        <Container size="full">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-7">
              <span className="section-title__tagline inline-block mb-3">
                {audienceTag}
              </span>
              <H2
                id="lecture-photos-heading"
                className="mb-3 md:mb-4"
              >
                {t('lectures.detail.photosHeading')}
              </H2>
            </div>
          </Reveal>
          <Reveal>
            {photo1Src && photo2Src ? (
              // 2-col grid; cap the row at ~1480px so each ≈720px on desktop.
              <div className="grid sm:grid-cols-2 gap-4 lg:gap-6 max-w-[1480px] mx-auto">
                <figure>
                  <a
                    href={photo1Src}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('lectures.detail.openPhoto')}
                    className="block focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-4 rounded-yada-lg"
                  >
                    <img
                      src={photo1Src}
                      srcSet={photo1SrcSet}
                      sizes={photo1SrcSet ? PHOTO_GRID_SIZES : undefined}
                      alt={photo1Alt}
                      loading="lazy"
                      decoding="async"
                      width={lecture.photo1Width}
                      height={lecture.photo1Height}
                      className="block w-full h-auto rounded-yada-md md:rounded-yada-lg border border-border shadow-yada hover:shadow-yada-lg transition-shadow aspect-[3/2] object-cover"
                    />
                  </a>
                </figure>
                <figure>
                  <a
                    href={photo2Src}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('lectures.detail.openPhoto')}
                    className="block focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-4 rounded-yada-lg"
                  >
                    <img
                      src={photo2Src}
                      srcSet={photo2SrcSet}
                      sizes={photo2SrcSet ? PHOTO_GRID_SIZES : undefined}
                      alt={photo2Alt}
                      loading="lazy"
                      decoding="async"
                      width={lecture.photo2Width}
                      height={lecture.photo2Height}
                      className="block w-full h-auto rounded-yada-md md:rounded-yada-lg border border-border shadow-yada hover:shadow-yada-lg transition-shadow aspect-[3/2] object-cover"
                    />
                  </a>
                </figure>
              </div>
            ) : (
              // Single photo (photo1 or photo2) — cap at 720px and center.
              <figure className="mx-auto max-w-[720px]">
                <a
                  href={photo1Src ?? photo2Src}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('lectures.detail.openPhoto')}
                  className="block focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-4 rounded-yada-lg"
                >
                  <img
                    src={photo1Src ?? photo2Src}
                    srcSet={photo1Src ? photo1SrcSet : photo2SrcSet}
                    sizes={(photo1Src ? photo1SrcSet : photo2SrcSet) ? PHOTO_SINGLE_SIZES : undefined}
                    alt={photo1Src ? photo1Alt : photo2Alt}
                    loading="lazy"
                    decoding="async"
                    width={photo1Src ? lecture.photo1Width : lecture.photo2Width}
                    height={photo1Src ? lecture.photo1Height : lecture.photo2Height}
                    className="block w-full h-auto rounded-yada-md md:rounded-yada-lg border border-border shadow-yada hover:shadow-yada-lg transition-shadow aspect-[3/2] object-cover"
                  />
                </a>
              </figure>
            )}
          </Reveal>
        </Container>
      </section>
      )}

      {/* Body — ONE markdown body (2026-10-29: the 分段落 feature and its
          table of contents are gone; `## ` headings in the markdown carry
          the sections now). */}
      {bodyMd.trim() && (
      <section className="py-section-y px-section-x bg-white" aria-labelledby="lecture-body-heading">
        <Container size="lg">
          <h2
            id="lecture-body-heading"
            className="sr-only"
          >
            {t('lectures.detail.bodyHeading')}
          </h2>
          <div className="max-w-3xl mx-auto">
            {bodyBlocks.map((block, bi) => {
              if (block.kind === 'heading') {
                // A heading may only hold phrasing content, so a `{{shortcode}}`
                // embed inside one is lifted OUT into a sibling block.
                return block.level === 3 ? (
                  <ShortcodeBlock key={bi} as="h3" className="text-xl font-bold mt-8 mb-3 text-ink" text={block.text} options={{ imageSizes: BODY_IMAGE_SIZES }} />
                ) : (
                  <ShortcodeBlock key={bi} as="h2" className="text-2xl font-bold mt-10 mb-4 text-ink" text={block.text} options={{ imageSizes: BODY_IMAGE_SIZES }} />
                )
              }
              if (block.kind === 'image') {
                // A body image is usually a media-library upload, so its uuid
                // can be recovered from the path: the native size then reserves
                // the box (no layout shift) and the responsive ladder gives
                // the browser a choice of files. A path that is not a library
                // upload keeps the plain <img> it has always been.
                const uuid = uuidFromUploadPath(block.src)
                const entry = uuid ? cmsMediaIndex[uuid] : undefined
                const srcSet = entry ? cmsMediaSrcSet(uuid) : undefined
                return (
                  <figure key={bi} className="my-7">
                    <img
                      src={block.src}
                      srcSet={srcSet}
                      sizes={srcSet ? BODY_IMAGE_SIZES : undefined}
                      alt={block.alt}
                      loading="lazy"
                      decoding="async"
                      width={entry?.width ?? undefined}
                      height={entry?.height ?? undefined}
                      className="block w-full h-auto rounded-yada-md md:rounded-yada-lg border border-border shadow-yada"
                    />
                    {block.alt && (
                      <figcaption className="mt-2 text-sm text-ink-muted">
                        {block.alt}
                      </figcaption>
                    )}
                  </figure>
                )
              }
              if (block.kind === 'list') {
                const ordered = block.ordered
                const Tag = ordered ? 'ol' : 'ul'
                return (
                  <Tag key={bi} className="list-disc pl-6 my-4 space-y-1 text-lg text-ink-soft leading-relaxed">
                    {block.items.map((item, ii) => (
                      <li key={ii}>{renderInlineMarkdownWithShortcodes(item, { imageSizes: BODY_IMAGE_SIZES })}</li>
                    ))}
                  </Tag>
                )
              }
              if (block.kind === 'quote') {
                return (
                  <blockquote
                    key={bi}
                    className="border-l-4 border-primary pl-4 my-6 italic text-lg text-ink-soft"
                  >
                    {renderInlineMarkdownWithShortcodes(block.text, { imageSizes: BODY_IMAGE_SIZES })}
                  </blockquote>
                )
              }
              // Same for a paragraph — the embed is block-level content and
              // may not sit inside a <p>. See ShortcodeBlock.
              return (
                <ShortcodeBlock
                  key={bi}
                  as="p"
                  className="text-lg text-ink-soft leading-relaxed whitespace-pre-line mb-4 last:mb-0"
                  text={block.text}
                  options={{ imageSizes: BODY_IMAGE_SIZES }}
                />
              )
            })}
          </div>
        </Container>
      </section>
      )}

      {/* Closing CTA — back to archive */}
      <section className="py-section-y px-section-x bg-gradient-to-br from-primary via-primary-dark to-yada-olive text-surface">
        <Container>
          <Reveal>
            <div className="max-w-3xl mx-auto text-center">
              <Icon
                icon="lucide:arrow-left"
                className="text-accent mx-auto mb-6"
                style={{ fontSize: '3rem' }}
              />
              <Link
                to="/lectures"
                className="inline-flex items-center gap-2 bg-accent text-ink px-7 py-3 rounded-yada-full font-semibold hover:bg-accent-soft transition-colors"
              >
                <Icon icon="lucide:arrow-left" />
                {t('lectures.detail.backToArchive')}
              </Link>
              {externalHref && (
                <a
                  href={externalHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-3 inline-flex items-center gap-2 bg-transparent border-2 border-surface text-surface px-7 py-3 rounded-yada-full font-semibold hover:bg-surface hover:text-primary transition-colors"
                >
                  <Icon icon="lucide:external-link" />
                  {t('lectures.detail.readOnSite')}
                </a>
              )}
            </div>
          </Reveal>
        </Container>
      </section>
    </article>
  )
}
